import React, { useEffect, useState } from 'react';
import { Table, Button, Toast, Spin, Tag, Select, Modal, Tooltip } from '@douyinfe/semi-ui';
import { API } from '../helpers';
import { matchPath, navigate } from '../router';

const RANGE_OPTIONS = [
  { value: 'today', label: '今日' },
  { value: '7d',    label: '近 7 天' },
  { value: '30d',   label: '近 30 天' },
  { value: 'all',   label: '全部' },
];

const STATUS_OPTIONS = [
  { value: '',  label: '全部状态' },
  { value: '1', label: '成功' },
  { value: '3', label: '断开' },
  { value: '0', label: '失败' },
];

function maskKey(k) {
  if (!k) return '';
  return k.slice(0, 6) + '...' + k.slice(-6);
}

// 把任意值兜底转成可读字符串
// 历史上 prompt_text 可能被存成对象/数组/字符串混在一起 — 渲染前统一处理,避免出现 [object Object]
function safeText(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    const j = JSON.stringify(v);
    return j.length > 200 ? j.slice(0, 200) + '…' : j;
  } catch {
    return '';
  }
}

function fmtQuota(n) {
  return Number(n || 0).toLocaleString();
}

function fmtTime(ms) {
  if (!ms) return '—';
  const d = new Date(ms);
  return d.toLocaleString('zh-CN', { hour12: false });
}

// 时长格式化:22小时59分 / 5分30秒
function fmtDur(ms) {
  const v = Number(ms || 0);
  if (!v || v < 0) return '—';
  const s = Math.round(v / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}天${h}小时`;
  if (h > 0) return `${h}小时${m}分`;
  if (m > 0) return `${m}分${sec}秒`;
  return `${sec}秒`;
}

// 大数字压缩:2.6 亿 / 14.7 万
function fmtBig(n) {
  const v = Number(n || 0);
  if (v >= 1e8) return (v / 1e8).toFixed(2) + ' 亿';
  if (v >= 1e4) return (v / 1e4).toFixed(1) + ' 万';
  return v.toLocaleString();
}

// 限额进度条(5h / 周)
const LimitBar = ({ label, used, limit, resetText }) => {
  const lim = Number(limit || 0);
  const use = Number(used || 0);
  if (!lim) return null;
  const pct = Math.min(100, (use / lim) * 100);
  const color = pct >= 90 ? '#f56565' : (pct >= 70 ? '#ff7d00' : '#22c55e');
  return (
    <div style={{ flex: '1 1 280px', minWidth: 260 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, marginBottom: 6 }}>
        <span style={{ color: '#86909c' }}>{label}</span>
        <span style={{ fontWeight: 600, fontSize: 13 }}>
          {use.toLocaleString()}
          <span style={{ color: '#c9cdd4', fontWeight: 400 }}> / {lim.toLocaleString()}</span>
          <span style={{ color: '#86909c', fontWeight: 400, marginLeft: 8 }}>{resetText}</span>
        </span>
      </div>
      <div style={{ height: 6, background: '#f2f3f5', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: color, borderRadius: 3, transition: 'width .3s' }} />
      </div>
    </div>
  );
};

// 小标签行(基本信息)
const InfoCell = ({ label, value, color }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span style={{ color: '#86909c', fontSize: 12 }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 500, color: color || '#18181b' }}>{value}</span>
  </span>
);

const AdminTokenDetail = () => {
  const path = window.location.pathname;
  const params = matchPath('/admin/tokens/:id', path) || {};
  const tokenId = +params.id;

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [page, setPage] = useState({ current: 1, pageSize: 20, total: 0 });
  const [range, setRange] = useState('7d');
  const [statusFilter, setStatusFilter] = useState('');
  const [promptModal, setPromptModal] = useState(null); // null 或 { id, seq, model, prompt_text, created_at }
  // 协议签署状态(《服务使用知情确认书》):已签/未签/免签
  const [agree, setAgree] = useState(null);

  // 打开"用户提问"详情模态框
  const openPromptModal = (record) => {
    setPromptModal({
      id: record.id,           // 保留 logs.id,供运维查 DB 定位用
      seq: record.seq,         // token 内调用序号,展示给管理员看
      model: record.model,
      created_at: record.created_at,
      prompt_text: safeText(record.prompt_text),
    });
  };

  const loadInfo = () => {
    setLoading(true);
    API.get(`/api/admin/tokens/${tokenId}`)
      .then((r) => setInfo(r.data.data))
      .catch((e) => Toast.error(e.response?.data?.message || '加载失败'))
      .finally(() => setLoading(false));
  };

  const loadLogs = (p = page.current, s = page.pageSize) => {
    setLogsLoading(true);
    const params = new URLSearchParams({ range, page: String(p), page_size: String(s) });
    if (statusFilter) params.set('status', statusFilter);
    API.get(`/api/admin/tokens/${tokenId}/logs?${params.toString()}`)
      .then((r) => {
        const d = r.data.data || {};
        setLogs(d.items || []);
        setPage((prev) => ({ ...prev, current: d.page || 1, pageSize: d.page_size || s, total: d.total || 0 }));
      })
      .catch((e) => Toast.error(e.response?.data?.message || '加载日志失败'))
      .finally(() => setLogsLoading(false));
  };

  useEffect(() => {
    if (!tokenId) return;
    loadInfo();
  }, [tokenId]);

  // 协议签署状态(只读):这张卡签没签《服务使用知情确认书》
  useEffect(() => {
    if (!tokenId) return;
    API.get(`/api/admin/tokens/${tokenId}/agreement`)
      .then((r) => setAgree(r.data.data))
      .catch(() => {});
  }, [tokenId]);

  useEffect(() => {
    if (!tokenId) return;
    loadLogs(1, page.pageSize);
  }, [range, statusFilter, tokenId]);

  // 从「在飞请求」面板点令牌进来时带 focus=logs,直接滚到调用日志区,
  // 免得管理员还要自己往下滑一屏才看到记录。
  //
  // ⚠️ 以前这个 effect 只依赖 [tokenId],而首次执行时页面还在 loading
  //    (组件在下面直接 return <Spin/>),日志区根本没渲染 → 找不到元素 → 不滚动,
  //    跳转意图完全失效。所以要等 loading 结束(info 到位)再滚,
  //    并留一个重试兜底(日志区可能在日志接口返回后才挂上)。
  useEffect(() => {
    if (loading) return undefined;
    const focus = new URLSearchParams(window.location.search).get('focus');
    if (focus !== 'logs') return undefined;
    let tries = 0;
    let timer = null;
    const tryScroll = () => {
      const el = document.getElementById('token-logs');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (++tries < 12) timer = setTimeout(tryScroll, 100);
    };
    timer = setTimeout(tryScroll, 80);
    return () => { if (timer) clearTimeout(timer); };
  }, [tokenId, loading, info]);

  if (loading) return <Spin spinning />;
  if (!info) return <div style={{ padding: 20, color: '#86909c' }}>Token 不存在或加载失败</div>;

  const t = info.token || {};
  const sum = info.summary || {};
  const win = info.windows || {};
  const ins = info.insight || {};
  const dur = ins.duration || {};
  const ctx = ins.context || {};
  const lim = ins.limits || {};

  const columns = [
    // token 内调用序号(按 created_at 升序连续编号,与全局 logs.id 解耦)
    // 宽度给到 80:序号是 4~5 位数,60 会挤到换行
    { title: '序号', dataIndex: 'seq', width: 80, render: (s) => <span style={{ color: '#1d7dfa', fontWeight: 500 }}>{s}</span> },
    { title: '时间', dataIndex: 'created_at', width: 150, render: fmtTime },
    { title: '模型', dataIndex: 'model', width: 140 },
    {
      // 输入 token + 前缀缓存命中。命中那部分**不需要重新预填充(prefill)**,
      // 是长上下文场景下最值钱的一笔优化 —— 这里直接显示命中量与命中率,
      // 一眼能看出"这次是不是在吃缓存",而不是靠猜。
      title: '输入 token', dataIndex: 'prompt_tokens', width: 170,
      render: (n, r) => {
        const total = Number(n || 0);
        const cached = Number(r.cached_tokens || 0);
        if (!total) return <span style={{ color: '#c9cdd4' }}>—</span>;
        if (!cached) return <span style={{ color: '#18181b' }}>{total.toLocaleString()}</span>;
        // ⚠ 两种上游协议的 usage 语义不同:
        //   OpenAI 形状   : prompt_tokens 已含缓存, cached 是它的子集
        //   Anthropic 形状: prompt_tokens 只是「没命中缓存」的部分, cached 是并列的另一部分
        // 所以当 cached > total 时,总输入 = total + cached,命中率要按总输入算,
        // 否则会算出「30271%」这种数(旧数据里就是这么来的)。
        const denom = cached <= total ? total : total + cached;
        const pct = Math.round((cached / denom) * 100);
        return (
          <Tooltip content={
            <div style={{ lineHeight: 1.8 }}>
              总输入 {denom.toLocaleString()} token<br />
              缓存命中 {cached.toLocaleString()}({pct}%)<br />
              这部分跳过了预填充
            </div>
          }>
            <span style={{ color: '#1d7dfa', fontWeight: 500, cursor: 'help' }}>
              {denom.toLocaleString()}
              <span style={{ color: '#86909c', fontSize: 11, marginLeft: 4 }}>
                ⚡{pct}%
              </span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '用户提问', dataIndex: 'prompt_text', width: 320,
      render: (raw, record) => {
        const p = safeText(raw);
        if (!p) return <span style={{ color: '#c9cdd4' }}>—</span>;
        return (
          <span
            onClick={() => openPromptModal(record)}
            style={{
              color: '#18181b', fontSize: 12, cursor: 'pointer',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word',
              lineHeight: 1.5, maxHeight: '3em',
            }}
            title="点击查看完整内容"
          >
            {p}
          </span>
        );
      },
    },
    { title: '扣除额度', dataIndex: 'quota', width: 100, render: (q) => <span style={{ color: '#f56565' }}>{fmtQuota(q)}</span> },
    {
      // 深度思考 token。MiniMax-M3.1-Flash 默认开启深度思考,completion_tokens 里
      // 绝大部分是思考 token —— 正式回答常常只有几十个字。
      // 不显示这一列,后台就只看到「输出 30 token 却等了 261 秒」,完全看不出时间去哪了。
      title: '思考 token', dataIndex: 'reasoning_tokens', width: 120,
      render: (n) => n ? <span style={{ color: '#ff7d00', fontWeight: 500 }}>{Number(n).toLocaleString()}</span> : <span style={{ color: '#c9cdd4' }}>—</span>,
    },
    { title: '耗时', dataIndex: 'duration_ms', width: 90, render: (d) => d ? (d > 1000 ? (d / 1000).toFixed(2) + 's' : d + 'ms') : '—' },
    {
      title: '状态', dataIndex: 'status', width: 150,
      // status=3 = 客户端主动断开:不是失败(锅在客户端,不是网关没做好),
      // 也不是成功(内容没拿完)。用橙色区分,别再显示成"失败 / HTTP 0"。
      render: (s, r) => (
        <span>
          <Tag color={s === 1 ? 'green' : (s === 3 ? 'orange' : (s === 2 ? 'grey' : 'red'))} size="small">
            {s === 1 ? '成功' : (s === 3 ? '断开' : (s === 2 ? '拦截' : '失败'))}
          </Tag>
          <span style={{ marginLeft: 6, color: '#86909c', fontSize: 11 }}>HTTP {r.http_status}</span>
          {/* 限流拒绝按「同一张卡 + 5 分钟桶」合并成一行,这里标出合并了几次 */}
          {r.hit_count > 1 && (
            <Tooltip content={`同一 5 分钟窗口内合并了 ${r.hit_count} 次同类限流拒绝`}>
              <Tag color="orange" size="small" style={{ marginLeft: 6 }}>×{r.hit_count}</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      // 渠道显示**名字**而不是自增 ID:ID 是内部主键、删了不回收号,越跳越难看,
      // 日志里直接写渠道名更容易对上「渠道管理」里的那一行。
      // 渠道被删掉的历史日志显示 #id(已删除)。
      title: '渠道', dataIndex: 'channel_name', width: 190, ellipsis: true,
      render: (name, row) => (
        <span title={name || (row.channel_id ? `#${row.channel_id}(渠道已删除)` : '')}>
          {name || (row.channel_id ? `#${row.channel_id}(已删除)` : '—')}
        </span>
      ),
    },
    { title: '流式', dataIndex: 'is_stream', width: 60, render: (s) => s ? '是' : '否' },
    {
      title: '错误', dataIndex: 'error', width: 240,
      render: (e) => e ? <span style={{ color: '#f56565', fontSize: 12, wordBreak: 'break-all' }}>{e}</span> : '—',
    },
  ];

  return (
    <div>
      {/* 顶部:标题在左,Key+复制+返回按钮在右 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
            令牌详情 #{t.id} · {t.name}
          </h2>
          <Tag color={t.status === 1 ? 'green' : 'red'} size="small">{t.status_text}</Tag>
          {agree ? (
            <Tag
              color={agree.required ? (agree.signed ? 'green' : 'orange') : 'default'}
              size="small"
              theme="light"
            >
              {agree.required ? (agree.signed ? '协议已签' : '协议待签') : '协议免签'}
            </Tag>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button
            size="small" type="primary" theme="light"
            onClick={() => navigate('/admin/tokens')}
          >← 返回列表</Button>
        </div>
      </div>

      {/* 核心额度 + 调用 + 对话时长 + 上下文水位 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: 18, border: '1px solid #e5e6eb' }}>
          <div style={{ color: '#86909c', fontSize: 12 }}>剩余额度</div>
          <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6, color: t.unlimited ? '#1d7dfa' : '#18181b' }}>
            {t.unlimited ? '不限' : fmtQuota(t.remain_quota) + ' 额度'}
          </div>
          <div style={{ color: '#86909c', fontSize: 11, marginTop: 4 }}>
            {t.unlimited ? '永久有效' : `总额度 ${fmtQuota(t.remain_quota + t.used_quota)} · 已使用 ${fmtQuota(t.used_quota)}`}
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 10, padding: 18, border: '1px solid #e5e6eb' }}>
          <div style={{ color: '#86909c', fontSize: 12 }}>累计调用</div>
          <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6 }}>{fmtQuota(sum.total_calls)} 次</div>
          <div style={{ color: '#86909c', fontSize: 11, marginTop: 4 }}>
            成功 <span style={{ color: '#00b42a' }}>{fmtQuota(sum.success_calls)}</span> / 失败 <span style={{ color: '#f56565' }}>{fmtQuota(sum.failed_calls)}</span>
            <span style={{ marginLeft: 8, color: '#c9cdd4' }}>·</span>
            断开 <span style={{ color: '#ff7d00' }}>{fmtQuota(sum.aborted_calls || 0)}</span>
            <span style={{ marginLeft: 8, color: '#c9cdd4' }}>·</span>
            今日 <span style={{ color: '#18181b' }}>{fmtQuota(win.today?.calls || 0)}</span> 次
          </div>
        </div>

        {/* 对话时长:三个口径一起给 —— 跨度是客户感知的,活跃是真正在聊的,
            上游耗时才是解释账单的(计费 = 倍率 × ceil(上游耗时/步长))。 */}
        <div style={{ background: '#fff', borderRadius: 10, padding: 18, border: '1px solid #e5e6eb' }}>
          <div style={{ color: '#86909c', fontSize: 12 }}>对话时长</div>
          <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6, color: '#18181b' }}>
            {dur.sessions ? fmtDur(dur.active_ms) : '—'}
          </div>
          <div style={{ color: '#86909c', fontSize: 11, marginTop: 4, lineHeight: 1.7 }}>
            跨度 <span style={{ color: '#18181b' }}>{fmtDur(dur.span_ms)}</span>
            <span style={{ margin: '0 6px', color: '#c9cdd4' }}>·</span>
            上游 <span style={{ color: '#18181b' }}>{fmtDur(dur.busy_ms)}</span>
            <span style={{ margin: '0 6px', color: '#c9cdd4' }}>·</span>
            <span style={{ color: '#18181b' }}>{dur.sessions}</span> 场对话
          </div>
        </div>

        {/* 上下文水位:主数字 = **当前水位** —— 最近一次真正带上下文的调用的输入 token。
            卡片上只留「当前水位」和「门槛 / 还差」两个数,一眼能看出离长上下文溢价线还有多远;
            历史峰值 / 本场平均 / 累计 token 收进标题旁的 ⓘ 悬停提示。
            (历史峰值本来是主数字,但用户新建对话后上下文会清零,拿它当「水位」会严重高估。) */}
        <div style={{ background: '#fff', borderRadius: 10, padding: 18, border: '1px solid #e5e6eb' }}>
          <div style={{ color: '#86909c', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            上下文水位
            <Tooltip content={
              <div style={{ lineHeight: 1.8 }}>
                当前水位 {fmtBig(ctx.current_prompt_tokens)}（最近一次调用的输入 token）<br />
                历史峰值 {fmtBig(ctx.peak_prompt_tokens)}{ctx.peak_headroom < 0 ? '（曾超窗）' : ''}<br />
                本场平均 {fmtBig(ctx.session_avg_prompt_tokens)}<br />
                累计 token {fmtBig(ctx.sum_total_tokens)}
              </div>
            }>
              <span style={{ cursor: 'help', color: '#c9cdd4' }}>ⓘ</span>
            </Tooltip>
          </div>
          <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6, color: '#18181b' }}>
            {ctx.current_prompt_tokens ? fmtBig(ctx.current_prompt_tokens) : '—'}
          </div>
          <div style={{ color: '#86909c', fontSize: 11, marginTop: 4, lineHeight: 1.7 }}>
            {ctx.ctx_limit > 0
              ? (ctx.headroom >= 0
                ? <>
                  门槛 <span style={{ color: '#18181b' }}>{fmtBig(ctx.ctx_limit)}</span>
                  <span style={{ margin: '0 6px', color: '#c9cdd4' }}>·</span>
                  还差 <span style={{ color: '#ff7d00' }}>{fmtBig(ctx.headroom)}</span>
                </>
                : <span style={{ color: '#f56565' }}>已超窗口 {fmtBig(Math.abs(ctx.headroom))}，按溢价计费</span>)
              : <span style={{ color: '#c9cdd4' }}>渠道未配置上下文门槛</span>}
          </div>
        </div>
      </div>

      {/* 速率限额进度(与后台「渠道用量」同一口径) */}
      {lim.limit_5h > 0 || lim.limit_week > 0 ? (
        <div style={{ background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #e5e6eb', marginBottom: 12, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <LimitBar label="5 小时限额" used={lim.quota_5h} limit={lim.limit_5h} resetText={lim.next_reset_5h_at ? fmtTime(lim.next_reset_5h_at) + ' 重置' : ''} />
          <LimitBar label="本周限额" used={lim.quota_week} limit={lim.limit_week} resetText={lim.next_reset_week_at ? fmtTime(lim.next_reset_week_at) + ' 重置' : ''} />
        </div>
      ) : null}

      {/* 基本信息 + 最后调用时间 */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 14, border: '1px solid #e5e6eb', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <InfoCell label="套餐" value={t.plan_name || '—'} />
        {t.sold_to ? <InfoCell label="售出" value={t.sold_to} color="#1d7dfa" /> : null}
        {t.remark ? <InfoCell label="备注" value={t.remark} color="#86909c" /> : null}
        <InfoCell label="到期" value={t.effective_expires_at ? fmtTime(t.effective_expires_at) : '不限'} />
        <InfoCell label="最后调用" value={sum.last_call_at ? fmtTime(sum.last_call_at) : '从未调用'} />
        {agree && agree.required ? (
          <InfoCell
            label="知情确认书"
            value={
              agree.signed
                ? `${fmtTime(agree.agreed_at)}${agree.ip ? ` · ${agree.ip}` : ''}`
                : '未签署'
            }
            color={agree.signed ? '#00b42a' : '#ff7d00'}
          />
        ) : null}
      </div>

      {/* 详细日志表 */}
      <div id="token-logs" style={{ background: '#fff', borderRadius: 10, padding: 8, border: '1px solid #e5e6eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8 }}>
          <strong>调用日志</strong>
          <Select
            style={{ width: 120 }}
            optionList={RANGE_OPTIONS}
            value={range}
            onChange={setRange}
          />
          <Select
            style={{ width: 110 }}
            optionList={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <span style={{ color: '#86909c', fontSize: 12 }}>共 {page.total} 条</span>
        </div>
        <Spin spinning={logsLoading}>
          <Table
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={logs}
            pagination={{
              currentPage: page.current,
              pageSize: page.pageSize,
              total: page.total,
              showSizeChanger: true,
              pageSizeOpts: [10, 20, 50, 100],
              onPageSizeChange: (s) => loadLogs(1, s),
              onPageChange: (p) => loadLogs(p, page.pageSize),
            }}
          />
        </Spin>
      </div>

      {/* 用户提问详情模态框 */}
      <Modal
        title={promptModal ? `用户提问 #${promptModal.seq ?? promptModal.id}` : '用户提问'}
        visible={!!promptModal}
        onCancel={() => setPromptModal(null)}
        onOk={() => setPromptModal(null)}
        okText="关闭"
        cancelButtonProps={{ style: { display: 'none' } }}
        width={720}
        style={{ top: 40 }}
      >
        {promptModal && (
          <div>
            <div style={{ fontSize: 12, color: '#86909c', marginBottom: 12 }}>
              模型:<Tag size="small" color="blue" style={{ marginLeft: 4 }}>{promptModal.model}</Tag>
              时间:<span style={{ marginLeft: 4 }}>
                {new Date(promptModal.created_at).toLocaleString('zh-CN', { hour12: false })}
              </span>
              字符数:<span style={{ marginLeft: 4, fontWeight: 500 }}>{promptModal.prompt_text.length}</span>
            </div>
            <div
              style={{
                background: '#fafbfc',
                border: '1px solid #e5e6eb',
                borderRadius: 6,
                padding: 12,
                fontSize: 13,
                color: '#1d2129',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 480,
                overflowY: 'auto',
              }}
            >
              {promptModal.prompt_text || <span style={{ color: '#c9cdd4' }}>(无内容)</span>}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminTokenDetail;